// Pure logic for the ReviewQueue N-up per-candidate review page. Kept separate
// from the React view so grouping, the per-candidate rating<->vote mapping, the
// PENDING/authorization lifecycle, and the reset request are unit-testable without
// rendering.
//
// #584 triple-review rework (rq-triple-review-contract.md): the A/B/EQUAL
// comparative verdict is RETIRED as the data model (AD-1). Each candidate
// ReviewItem is rated INDEPENDENTLY (usable / not-usable, mapped onto the existing
// ReviewVote decision APPROVE/REJECT + isBest), editable only while the item is
// PENDING and the caller is authorized for the question (AD-2/AD-3), read-only
// after finalize until an admin reset reopens the whole question (AD-3/AD-6).

// The independent per-candidate rating. Maps onto the existing ReviewVote decision
// so no schema change is needed (AD-1): USABLE↔APPROVE, NOT_USABLE↔REJECT.
export const RATING = {
  USABLE: "USABLE",
  NOT_USABLE: "NOT_USABLE",
};

// Dimensions a reviewer can tag to make "why" concrete rather than generic.
export const DIMENSIONS = [
  "accuracy",
  "labeling",
  "clarity",
  "artifacts",
  "relevance",
];

function candidateSortKey(item) {
  const p = item.payload || {};
  return String(p.candidateId || item.id || "");
}

// Group the flat queue (one item per candidate) into per-question sets, keyed by
// subjectKey (=questionId). Candidates inside a group are ordered by candidateId
// (falling back to item id) so cell order is stable across reloads. The whole
// candidate set is kept — AD-5 returns ALL candidates (decided included); the view
// renders each in its own cell (no top-2 slice).
export function groupBySubject(items) {
  const bySubject = new Map();
  (items || []).forEach((item) => {
    const key = item.subjectKey;
    if (!bySubject.has(key)) bySubject.set(key, []);
    bySubject.get(key).push(item);
  });
  const groups = [];
  bySubject.forEach((candidates, subjectKey) => {
    const ordered = [...candidates].sort((x, y) => {
      const xk = candidateSortKey(x);
      const yk = candidateSortKey(y);
      return xk < yk ? -1 : xk > yk ? 1 : 0;
    });
    // Every candidate shares the question's visualKind and its (per-question)
    // review assignment — fold both onto the group for the view.
    const visualKind = ordered.find((c) => c?.visualKind)?.visualKind || null;
    const assignment = ordered.find((c) => c?.assignment)?.assignment || null;
    const heldReason = ordered.find((c) => c?.heldReason)?.heldReason || null;
    // S3-U1: reviewer-visible grounding block + admin-only groundingStatus are QUESTION-level
    // on QuestionCandidatesDto — fold them onto the group (stamped per-candidate by
    // flattenQuestionsResponse, exactly like assignment/heldReason).
    const grounding = ordered.find((c) => c?.grounding)?.grounding || null;
    const groundingStatus = ordered.find((c) => c?.groundingStatus)?.groundingStatus || null;
    groups.push({ subjectKey, candidates: ordered, visualKind, assignment, heldReason, grounding, groundingStatus });
  });
  return groups;
}

// --- Visual-kind helpers (retained from the compare page) ---

// Human labels for the VisualKind names the backend sends (VisualKindClassifier).
export const VISUAL_KIND_LABELS = {
  IMAGE: "Image",
  TABLE_MD: "Table",
  TABLE_IMAGE: "Table (image)",
  DIAGRAM: "Diagram",
  NONE: "No visual",
  UNKNOWN: "Needs classification",
};

export function visualKindLabel(kind) {
  return VISUAL_KIND_LABELS[kind] || "Needs classification";
}

// DQ-014 F6: a self-explanatory filter-chip label. Akshay read the old `Image (1)`
// as an ID rather than a count; spell out "N question(s)" so the chip is unambiguous.
// Pure (kind → human label via visualKindLabel) so the JSX stays a one-liner and the
// singular/plural rule is unit-tested here rather than inlined in the render.
export function kindChipLabel(kind, count) {
  const n = Number.isFinite(Number(count)) ? Number(count) : 0;
  return `${visualKindLabel(kind)} · ${n} question${n === 1 ? "" : "s"}`;
}

// The ALL chip's counterpart: "All types · N" (no per-kind label). Same plural rule so
// the row reads consistently.
export function allKindChipLabel(count) {
  const n = Number.isFinite(Number(count)) ? Number(count) : 0;
  return `All types · ${n} question${n === 1 ? "" : "s"}`;
}

// The VisualKind names the frontend knows, in display order. UNKNOWN is the catch-all.
export const KNOWN_VISUAL_KINDS = ["IMAGE", "TABLE_MD", "TABLE_IMAGE", "DIAGRAM", "NONE", "UNKNOWN"];

// A known, tutor-facing visual kind: present AND not the UNKNOWN catch-all.
export function isKnownVisualKind(kind) {
  return Boolean(kind) && normalizeVisualKind(kind) !== "UNKNOWN";
}

// Whether to render the visual-kind tag/chip for a viewer. A known kind shows for
// everyone; UNKNOWN is admin-only chrome; an absent kind shows for no one.
export function showVisualKind(kind, isAdmin) {
  return isKnownVisualKind(kind) || Boolean(isAdmin && kind);
}

// Normalize a group's kind to a KNOWN bucket: null/absent OR a future/unrecognized
// backend enum value → UNKNOWN.
export function normalizeVisualKind(kind) {
  return KNOWN_VISUAL_KINDS.includes(kind) ? kind : "UNKNOWN";
}

// S1.2/P2-1: what the RAIL row shows for a kind. Known kinds keep their lowercase label;
// an admin-visible UNKNOWN collapses to a COMPACT marker ({ compact: true } → a "?" glyph
// carrying the "Needs classification" title) so ten unknown rows don't drown the list in
// repeated "needs classification" text. The question view keeps the full label (filter
// chips / admin chrome) — this helper is rail-only. Null = render nothing (tutor
// UNKNOWN/absent, unchanged).
export function railKindDisplay(kind, isAdmin) {
  if (!showVisualKind(kind, isAdmin)) return null;
  if (normalizeVisualKind(kind) === "UNKNOWN") return { compact: true };
  return { text: visualKindLabel(kind).toLowerCase() };
}

// Distinct visualKinds present across the groups (for building the filter row).
export function presentVisualKinds(groups) {
  const present = new Set((groups || []).map((g) => normalizeVisualKind(g.visualKind)));
  return KNOWN_VISUAL_KINDS.filter((k) => present.has(k));
}

// Filter groups to a kind; "ALL"/null → unfiltered.
export function filterGroupsByKind(groups, kind) {
  if (!kind || kind === "ALL") return groups || [];
  return (groups || []).filter((g) => normalizeVisualKind(g.visualKind) === kind);
}

// --- Per-candidate rating model (AD-1) ---

// USABLE↔APPROVE, NOT_USABLE↔REJECT (reuses the existing ReviewVote decision so no
// schema change is required — the comparative verdict is gone, the vote stays).
export function ratingDecision(rating) {
  if (rating === RATING.USABLE) return "APPROVE";
  if (rating === RATING.NOT_USABLE) return "REJECT";
  return null;
}

export function ratingFromDecision(decision) {
  if (decision === "APPROVE") return RATING.USABLE;
  if (decision === "REJECT") return RATING.NOT_USABLE;
  return null; // NEUTRAL / unknown → no explicit usable/not-usable rating
}

export function ratingLabel(rating) {
  if (rating === RATING.USABLE) return "Usable";
  if (rating === RATING.NOT_USABLE) return "Not usable";
  return "";
}

// The caller's saved rating for a candidate, derived from their own vote on the
// item — { rating, isBest, comment } — or null when they haven't rated it.
export function savedRating(candidate) {
  const v = candidate?.myVote;
  if (!v) return null;
  return {
    rating: ratingFromDecision(v.decision),
    isBest: Boolean(v.isBest),
    comment: v.comment || "",
    // P2-8: the caller's OWN danger flag on this candidate, so the admin ⚑ toggle reflects
    // saved state (server projects it into myVote.dangerFlag; the cross-reviewer aggregate is separate).
    dangerFlag: Boolean(v.dangerFlag),
    // QIR B4 / I4: the caller's own accuracy checklist + verification source (server projects them
    // into myVote from type_signal). Null when unset; seed the checklist UI from these.
    accuracyChecklist: v.accuracyChecklist || null,
    verificationSource: v.verificationSource || null,
  };
}

// --- QIR B4: content classification (precisionClass) + accuracy checklist ---

// The ingest-time content classification of a candidate (parent design §3.3, A-3). ABSENT means
// UNSTAMPED — read back as null, NEVER defaulted to STANDARD (matches the server's A-3 semantics).
export const PRECISION_CLASS = { SPATIAL_CRITICAL: "SPATIAL_CRITICAL", STANDARD: "STANDARD" };

// The five structured accuracy-checklist items (contract §4). Order is the render order.
export const ACCURACY_CHECKLIST_ITEMS = [
  { key: "labelsCorrect", label: "Labels correct" },
  { key: "spatialRelationsCorrect", label: "Spatial relations correct" },
  { key: "orientationCorrect", label: "Orientation correct" },
  { key: "noFabricatedStructures", label: "No fabricated structures" },
  { key: "consistentWithReference", label: "Consistent with reference" },
];

// The verification-source types (contract §4; must stay in sync with the backend enum).
export const VERIFICATION_SOURCE_TYPES = [
  { key: "REFERENCE_ASSET", label: "Reference asset" },
  { key: "TEXTBOOK_DB", label: "Textbook / DB" },
  { key: "WEB_REFERENCE", label: "Web reference" },
];

// One candidate's precisionClass, or null when ABSENT (A-3: absent = unstamped, not STANDARD).
export function candidatePrecisionClass(candidate) {
  const raw = candidate?.payload?.precisionClass;
  if (raw == null || String(raw).trim() === "") return null;
  return String(raw).trim().toUpperCase();
}

// The SPATIAL_CRITICAL accuracy checklist is rendered only for spatial-critical candidates
// (§3.3: "never certify from memory"). Non-spatial candidates carry no checklist UI.
export function shouldRenderChecklist(candidate) {
  return candidatePrecisionClass(candidate) === PRECISION_CLASS.SPATIAL_CRITICAL;
}

// A candidate's checklist is COMPLETE iff the caller answered all five items (each present as a
// boolean). "Complete" means answered — not all-pass.
export function checklistComplete(candidate) {
  const cl = candidate?.myVote?.accuracyChecklist;
  if (!cl) return false;
  return ACCURACY_CHECKLIST_ITEMS.every((it) => typeof cl[it.key] === "boolean");
}

// Build the per-candidate vote-upsert body (POST /qlearn/review/item/{id}/vote).
// The comment field is omitted when empty so a blank note never clobbers a prior one.
// P2-8: dangerFlag rides the SAME body (server maps it into typeSignal, symmetric with the
// comparative decision's stampGroupSignal). Sent explicitly true/false so an un-toggle clears it.
export function ratingToVoteBody(state) {
  const s = state || {};
  const body = { decision: ratingDecision(s.rating), isBest: Boolean(s.isBest) };
  const comment = (s.comment || "").trim();
  if (comment) body.comment = comment;
  if (s.dangerFlag) {
    body.dangerFlag = true;
    const dn = (s.dangerNote || "").trim();
    if (dn) body.dangerNote = dn;
  }
  // QIR B4 / I4: the accuracy checklist + verification source ride the SAME vote body (server
  // shape-checks + stores them in type_signal). Omitted when absent so a plain rating never
  // clobbers them, and only the whitelisted keys are sent.
  if (s.accuracyChecklist && Object.keys(s.accuracyChecklist).length) {
    body.accuracyChecklist = s.accuracyChecklist;
  }
  if (s.verificationSource?.sourceType) {
    body.verificationSource = s.verificationSource;
  }
  // QIR B4 amendment (ruling a): request the non-finalizing rating-only server path (tutor
  // comparative evidence collection; also the F8 post-decision refine).
  if (s.ratingOnly) body.ratingOnly = true;
  return body;
}

// S4/T-R4: how many of a group's candidates carry an active (admin-only) danger flag.
// Admin surfaces only — tutors never receive the aggregate dangerFlagged field.
export function countDangerFlagged(group) {
  const cands = group?.candidates || [];
  return cands.reduce((n, c) => n + (c?.dangerFlagged ? 1 : 0), 0);
}

// P0-1d: is the pick-best WINNER one the reviewer has danger-flagged in-flight? Drives the
// decision-time confirm warning ("you are approving an image you flagged"). Uses the reviewer's
// OWN in-flight flag set, not server state.
export function winnerDangerFlagged(selectedId, dangerFlagIds) {
  if (!selectedId) return false;
  if (dangerFlagIds && typeof dangerFlagIds.has === "function") return dangerFlagIds.has(selectedId);
  return Array.isArray(dangerFlagIds) && dangerFlagIds.includes(selectedId);
}

// --- Lifecycle + authorization (AD-2 / AD-3, fail-closed) ---

// A candidate is editable only while it is PENDING. Absent status == PENDING (a
// pre-decision item), so it stays editable; any other status is finalized.
export function isItemPending(candidate) {
  const s = candidate?.reviewStatus;
  return !s || s === "PENDING";
}

export function isItemFinalized(candidate) {
  const s = candidate?.reviewStatus;
  return Boolean(s) && s !== "PENDING";
}

// AD-2: authorization to rate is per-question (subjectKey) reviewer assignment,
// FAIL-CLOSED. An admin may always rate. For a tutor the authoritative signal is the
// server-computed boolean `assignment.canRate` on QuestionCandidatesDto (reconciled
// against QLearn-service#579): tutors never receive `assignedReviewerIds` (admin-only,
// plural), so client-side identity matching is neither possible nor needed. No
// assignment info, or canRate truly absent with no legacy signal → NOT authorized
// (read-only). Mentorship is explicitly NOT consulted here.
export function isAuthorizedToRate(group, ctx) {
  const c = ctx || {};
  if (c.isAdmin) return true;
  const a = group?.assignment || null;
  if (!a) return false; // fail-closed
  // REAL backend signal (#579): server-computed per-caller boolean.
  if (typeof a.canRate === "boolean") return a.canRate;
  // Back-compat with earlier assumed shapes (pre-reconcile), harmless if unused.
  if (typeof a.authorized === "boolean") return a.authorized;
  if (a.assignedReviewerId != null) {
    return Boolean(a.active && String(a.assignedReviewerId) === String(c.viewerId));
  }
  return false; // canRate absent and no legacy signal → fail-closed
}

// Whether the question is assigned to the caller (for the "assigned to me" display,
// distinct from canRate which governs write access). Admins see all; tutors read the
// server's `assignment.assignedToMe`.
export function isAssignedToMe(group, ctx) {
  const c = ctx || {};
  if (c.isAdmin) return true;
  const a = group?.assignment || null;
  return Boolean(a?.assignedToMe);
}

// Editable iff the item is PENDING AND the caller is authorized for the question.
export function isCandidateEditable(candidate, group, ctx) {
  return isItemPending(candidate) && isAuthorizedToRate(group, ctx);
}

// The id of the candidate the caller flagged best (isBest is ≤1 per question,
// enforced server-side; this reads back the current pick), or null.
export function bestItemId(group) {
  const cands = group?.candidates || [];
  const best = cands.find((c) => {
    const s = savedRating(c);
    return s?.isBest;
  });
  return best ? best.id : null;
}

// --- Response classification (shared by every write endpoint on this page) ---

// All five of this page's write endpoints classify their response the same way, so
// the mapping lives here once and the exported names below alias it (call sites and
// tests use those names). No errStatus → "ok"; 404 → "dark" (the feature is flag-dark
// on this backend); anything else → "error", surfaced non-destructively.
function classifyOutcome(res) {
  if (!res?.errStatus) return "ok";
  return String(res.errStatus) === "404" ? "dark" : "error";
}

// The reset and group-decision endpoints additionally recognise 409 as "conflict":
// AD-6, a candidate is already PUBLISHED/RETRACTED (reset) or the question was decided
// by someone else (decision), so the caller must reload rather than reopen.
function classifyOutcomeWithConflict(res) {
  if (String(res?.errStatus) === "409") return "conflict";
  return classifyOutcome(res);
}

// --- Reset (AD-3 / AD-6) ---

// Body for POST /qlearn/review/questions/{subjectKey}/reset.
export function buildResetRequest(reason) {
  return { confirm: true, reason: (reason || "").trim() };
}

// Classify the reset response. AD-6: a 409 means a candidate is PUBLISHED/RETRACTED
// so the question can't be reset — surface it non-destructively, do NOT reopen. 404
// is the flag-dark case; any other error is a transient failure.
export const resetOutcome = classifyOutcomeWithConflict;

// --- Adapters for the all-candidates reads (AD-5, contract §5), reconciled to the
// REAL QLearn-service#579 DTOs ---
// GET /qlearn/review/questions/{subjectKey} → QuestionCandidatesDto → a group:
//   { subjectKey, reviewType, candidates: ReviewItemDto[],
//     assignment: { assignedToMe, canRate, assignedReviewerIds? (ADMIN-only) } }
// ReviewItemDto carries visualKind PER-CANDIDATE (group kind is derived from the
// candidates by groupBySubject, not a top-level field) and myVote { decision, isBest,
// isDuplicate?, keeperQuestionId? } | null. Tolerant: also accepts `items` instead of
// `candidates`, a bare array, and a legacy top-level `assignment`.
export function adaptQuestionResponse(res) {
  if (!res) return null;
  const subjectKey = res.subjectKey || res.questionId || null;
  const list = Array.isArray(res) ? res : res.candidates || res.items || [];
  const normalized = list.map((c) => ({ ...c, subjectKey: c?.subjectKey || subjectKey }));
  const g =
    groupBySubject(normalized)[0] || { subjectKey, candidates: [], visualKind: null, assignment: null };
  // Assignment is GROUP-level on QuestionCandidatesDto — prefer it over any per-item
  // fold (real ReviewItemDto carries no assignment).
  if (!Array.isArray(res) && res.assignment) g.assignment = res.assignment;
  // S3-U1: single-question reads carry grounding/groundingStatus at the top level too.
  if (!Array.isArray(res) && res.grounding) g.grounding = res.grounding;
  if (!Array.isArray(res) && res.groundingStatus) g.groundingStatus = res.groundingStatus;
  if (subjectKey) g.subjectKey = subjectKey;
  return g;
}

// Normalize the queue LIST response to flat candidate items (the shape the page's
// grouping pipeline consumes), stamping the GROUP-level assignment onto each of a
// question's candidates so authorization survives the flattening. Handles BOTH the
// real QuestionCandidatesPage { questions: QuestionCandidatesDto[], totalElements }
// and the legacy flat { items: ReviewItemDto[], totalElements } shape.
export function flattenQuestionsResponse(res) {
  if (!res || typeof res !== "object" || Array.isArray(res)) return { items: [], totalElements: 0 };
  if (Array.isArray(res.questions)) {
    const items = res.questions.flatMap((q) =>
      (q?.candidates || []).map((c) => ({
        ...c,
        subjectKey: c?.subjectKey || q?.subjectKey,
        // Group-level assignment stamped onto each candidate so groupBySubject can
        // fold it back (real ReviewItemDto has no per-item assignment).
        assignment: c?.assignment || q?.assignment || null,
        // A1 hold marker (admin-only server field) rides the same stamp-and-fold path.
        heldReason: q?.heldReason || null,
        // S3-U1 grounding (question-level): reviewer-visible references + admin-only status
        // ride the same stamp-and-fold path so groupBySubject can lift them to the group.
        grounding: c?.grounding || q?.grounding || null,
        groundingStatus: c?.groundingStatus || q?.groundingStatus || null,
      }))
    );
    return { items, totalElements: res.totalElements != null ? res.totalElements : items.length };
  }
  if (Array.isArray(res.items)) {
    return { items: res.items, totalElements: res.totalElements || 0 };
  }
  return { items: [], totalElements: 0 };
}

// Whether the server holds more QUESTIONS than this page returned — unit-safe per shape.
// On the QuestionCandidatesPage shape, totalElements counts QUESTIONS, so it must be
// compared to questions.length — never to the flattened candidate-card count (a page of
// multi-candidate questions would otherwise mask unfetched questions: 4 questions × 3
// candidates loaded of totalElements=10 → 10 > 12 reads "all loaded", wrongly). On the
// legacy flat shape, totalElements counts ITEMS and compares to items.length.
export function moreQuestionsOnServer(res) {
  if (!res || typeof res !== "object" || Array.isArray(res)) return false;
  if (Array.isArray(res.questions)) {
    return res.totalElements != null && res.totalElements > res.questions.length;
  }
  if (Array.isArray(res.items)) {
    return (res.totalElements || 0) > res.items.length;
  }
  return false;
}

// True when the response carries a recognizable candidate payload (either shape) —
// used to distinguish a real empty/valid page from an error/dark response.
export function hasQuestionsPayload(res) {
  return Boolean(res && (Array.isArray(res.items) || Array.isArray(res.questions)));
}

// --- Needs-generation partition (#584 S1-U1) ---
// The admin-only held-group partition on QuestionCandidatesPage: an array of
// { subjectKey, pendingCount, floor, topUpRequest: { status, neededCount, requestedAt } }.
// Tutors receive NO needsGeneration key (server sends null; the redaction choke-point also
// drops it), so this yields [] for them — the rail section never renders.
export function extractNeedsGeneration(res) {
  if (!res || typeof res !== "object" || Array.isArray(res)) return [];
  return Array.isArray(res.needsGeneration) ? res.needsGeneration : [];
}

// A human label for a held group's top-up request state (admin chrome). The server owns the
// status; the UI only renders it. OPEN → a request is out to the generation pipeline;
// CONSUMED → the pipeline took the last request but the group is short again; NONE → no
// request landed yet (fail-open emission retries on the next read).
export function topUpStatusLabel(topUpRequest) {
  const t = topUpRequest || {};
  const status = t.status || "NONE";
  if (status === "OPEN") {
    return t.neededCount ? `top-up requested (+${t.neededCount})` : "top-up requested";
  }
  if (status === "CONSUMED") return "top-up consumed — awaiting re-request";
  return "no top-up request yet";
}

// --- Group state: work vs completed (AD-5 keeps decided candidates visible) ---

export const GROUP_STATE = {
  TO_REVIEW: "TO_REVIEW",
  COMPLETED: "COMPLETED",
};

// Group state is DERIVED per-candidate (candidates finalize independently):
//   TO_REVIEW — ANY candidate still PENDING (`partial` marks a decided sibling).
//   COMPLETED — no candidate PENDING; status by precedence
//               RETRACTED > PUBLISHED > APPROVED (any) > REJECTED (all rejected).
export function groupReviewState(group) {
  const cands = ((group && group.candidates) || []).filter(Boolean);
  if (cands.length === 0) {
    return { state: GROUP_STATE.COMPLETED, status: null, partial: false };
  }
  const anyPending = cands.some((c) => isItemPending(c));
  const anyDecided = cands.some((c) => isItemFinalized(c));
  if (anyPending) {
    return { state: GROUP_STATE.TO_REVIEW, status: null, partial: anyDecided };
  }
  let status;
  if (cands.some((c) => c.publishStatus === "RETRACTED")) status = "RETRACTED";
  else if (cands.some((c) => c.publishStatus === "PUBLISHED")) status = "PUBLISHED";
  else if (cands.some((c) => c.reviewStatus === "APPROVED")) status = "APPROVED";
  else status = "REJECTED";
  return { state: GROUP_STATE.COMPLETED, status, partial: false };
}

// A fully-decided question is read-only (no candidate is PENDING); only an admin
// reset can reopen it.
export function isGroupReadOnly(group) {
  return groupReviewState(group).state === GROUP_STATE.COMPLETED;
}

// Partition loaded groups into still-reviewable work and completed (decided) sets.
export function splitGroupsByState(groups) {
  const work = [];
  const completed = [];
  (groups || []).forEach((g) => {
    if (groupReviewState(g).state === GROUP_STATE.TO_REVIEW) work.push(g);
    else completed.push(g);
  });
  return { work, completed };
}

// A compact rail glyph: the reviewer has rated at least one candidate → ✓, else null.
export function myRatingGlyph(group) {
  const cands = group?.candidates || [];
  return cands.some((c) => savedRating(c)) ? "✓" : null;
}

// MIXED-DEPLOY GUARD: the rail's DECIDED fetch may hit an old backend that ignores
// the status param and answers a PENDING page. Drop pending strays (derive from each
// item's OWN reviewStatus) and dedupe anything already in the work queue by id.
export function filterDecidedItems(items, workQueueIds) {
  const seen = workQueueIds instanceof Set ? workQueueIds : new Set(workQueueIds || []);
  return (items || []).filter(
    (it) => it && it.reviewStatus && it.reviewStatus !== "PENDING" && !seen.has(it.id)
  );
}

// --- Suitability (retained: per-question "no visual belongs here") ---

export function buildSuitabilityRequest(subjectKey, examType, state) {
  const s = state || {};
  const body = { questionId: subjectKey };
  if (examType) body.examType = examType;
  if (s.image !== undefined) {
    body.unsuitableForImage = s.image;
    const r = (s.imageReason || "").trim();
    if (s.image && r) body.imageReason = r;
  }
  if (s.table !== undefined) {
    body.unsuitableForTable = s.table;
    const r = (s.tableReason || "").trim();
    if (s.table && r) body.tableReason = r;
  }
  return body;
}

export const suitabilityOutcome = classifyOutcome;

// --- Admin reviewer-assignment panel (WI-2, AD-2) — pure helpers ---

// The admin-only current-assignee list for a question: the server-returned
// assignment.assignedReviewerIds (admin-only; tutors never receive it). Null/absent
// (a non-admin payload, or no assignment) → empty list. De-duped, string-normalized.
export function currentAssigneeIds(group) {
  const a = group?.assignment || null;
  const ids = a && Array.isArray(a.assignedReviewerIds) ? a.assignedReviewerIds : [];
  const seen = new Set();
  const out = [];
  ids.forEach((id) => {
    const s = id == null ? "" : String(id);
    if (s && !seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  });
  return out;
}

// Normalize the GET /qlearn/users?role=TUTOR roster response to a stable
// { id, displayName, email } list. Accepts a bare User[] (the real shape), a
// { users } / { userList } wrapper, tolerates missing name/email, and returns [] for
// an error/dark response ({ status }). Rows without an id are dropped (unassignable).
export function normalizeRoster(res) {
  if (!res || (typeof res === "object" && !Array.isArray(res) && res.status)) return [];
  const list = Array.isArray(res) ? res : res.users || res.userList || [];
  return (list || [])
    .filter((u) => u?.id != null)
    .map((u) => ({
      id: String(u.id),
      displayName: u.displayName || u.name || "",
      email: u.email || "",
    }));
}

// Case-insensitive search over a roster by display name OR email. Blank query → all.
// UUID is intentionally NOT matched (the picker is by name/email, per the contract).
export function searchRoster(roster, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return roster || [];
  return (roster || []).filter((u) => {
    const name = String(u.displayName || "").toLowerCase();
    const email = String(u.email || "").toLowerCase();
    return name.includes(q) || email.includes(q);
  });
}

// A human label for an assignee id, resolved through the roster. Falls back to a short
// id tail when the roster hasn't loaded or the user isn't in it (revoke still works by id).
export function assigneeLabel(reviewerId, roster) {
  const id = reviewerId == null ? "" : String(reviewerId);
  const u = (roster || []).find((x) => x && String(x.id) === id);
  if (u && (u.displayName || u.email)) {
    return u.email ? `${u.displayName || u.email} (${u.email})` : u.displayName;
  }
  return id.length > 8 ? `…${id.slice(-6)}` : id || "unknown";
}

// Classify an assign/revoke response: 404 → dark (feature off), any other errStatus →
// error, else ok. Mirrors resetOutcome so the panel surfaces failures non-destructively.
export const assignmentOutcome = classifyOutcome;

// DQ-014 F1: reconcile the panel's assignee list against SERVER truth. The panel used to
// seed chips only from the once-per-load group payload (assignment.assignedReviewerIds),
// so a just-added reviewer vanished on nav-away-and-back (the group was stale) — it read
// as "not persisted" even though the backend committed it. This maps the
// GET /assignments response (ReviewAssignmentDto[]) to the active assignedReviewerIds so
// the panel can read-after-write.
//
// doGetCall surfaces a non-200 as { status } (dark 404 = feature off, or any error). In
// that case we return `fallback` (default null) so the CALLER keeps the current
// group-derived assignees — identical to today's behavior when the endpoint is disabled
// (dark-safe, no regression). Only a real array replaces the list. De-duped +
// string-normalized to match currentAssigneeIds. active === false rows are dropped
// (defensive; the endpoint already returns active rows).
export function assigneeIdsFromAssignments(res, fallback = null) {
  if (!Array.isArray(res)) return fallback;
  const seen = new Set();
  const out = [];
  res.forEach((row) => {
    if (!row || row.active === false) return;
    const s = row.assignedReviewerId == null ? "" : String(row.assignedReviewerId);
    if (s && !seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  });
  return out;
}

// --- QIR B5 (§6.3): bulk assign by criteria — pure helpers (DAMP-tested) ---

// Server tutor-visibility modes (§6.2). The default is ALL_READY (dark ship); the flip to
// ASSIGNED_ONLY is a separate gated activation — this UI never flips it.
export const TUTOR_VISIBILITY_MODE = { ALL_READY: "ALL_READY", ASSIGNED_ONLY: "ASSIGNED_ONLY" };

// The "Assigned to you (n)" tutor rail renders ONLY under ASSIGNED_ONLY (inert pin): under
// ALL_READY (shipped default) it is invisible, so today's tutor rail is byte-unchanged.
export function railVisible(mode) {
  return mode === TUTOR_VISIBILITY_MODE.ASSIGNED_ONLY;
}

export function assignedToYouLabel(count) {
  return `Assigned to you (${Number.isFinite(count) ? count : 0})`;
}

export const BULK_ASSIGN_STRATEGIES = [
  { key: "ALL_TO_EACH", label: "All to each reviewer" },
  { key: "ROUND_ROBIN", label: "Round-robin (split evenly)" },
];

// Parse a free-text explicit subject list ("q-1, q-2  q-3") into a distinct id array; blank
// → undefined (so it never overrides criteria unless the admin actually typed keys).
export function parseSubjectKeys(text) {
  if (!text || typeof text !== "string") return undefined;
  const keys = text.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
  const distinct = Array.from(new Set(keys));
  return distinct.length ? distinct : undefined;
}

// Assemble the bulk-assign criteria object from console fields, dropping every empty field
// so the server sees ONLY the constraints the admin set (an absent field is not a filter).
// Explicit subjectKeys win server-side, but we still send the sibling fields as-entered.
export function buildBulkCriteria(fields = {}) {
  const c = {};
  const put = (k, v) => {
    if (v !== undefined && v !== null && String(v).trim() !== "") c[k] = typeof v === "string" ? v.trim() : v;
  };
  put("reviewType", fields.reviewType || "IMAGE_CANDIDATE");
  put("examType", fields.examType);
  put("organSystem", fields.organSystem);
  put("discipline", fields.discipline);
  put("visualKind", fields.visualKind);
  put("precisionClass", fields.precisionClass);
  put("state", fields.state);
  const subjectKeys = parseSubjectKeys(fields.subjectKeys);
  if (subjectKeys) c.subjectKeys = subjectKeys;
  return c;
}

// Classify a bulk assign/preview response: 404 → dark, any other errStatus (incl. 400
// over-cap) → error, else ok. Mirrors assignmentOutcome.
export const bulkAssignOutcome = classifyOutcome;

// Preview-before-commit gate (I1 UX mirror): Confirm is allowed ONLY when a fresh preview
// result exists for the current selection (matched something) AND ≥1 reviewer is chosen and
// nothing is in flight. A criteria/reviewer edit clears `preview` in the component, so this
// re-locks Confirm until the admin previews again — Confirm can never fire without a preview.
export function bulkConfirmEnabled({ preview, reviewerIds, busy }) {
  return Boolean(
    preview &&
      typeof preview.matchedCount === "number" &&
      preview.matchedCount > 0 &&
      Array.isArray(reviewerIds) &&
      reviewerIds.length > 0 &&
      !busy
  );
}

// --- Comparative group decision (#584 A2, T-CMP U1) — pure helpers ---

// The structured reject-reason categories (server enum, T-CMP B1). Labels map the
// retired per-candidate chips onto the group decision.
export const REJECT_CATEGORIES = [
  { key: "ACCURACY", label: "Accuracy" },
  { key: "LABELING", label: "Labeling" },
  { key: "CLARITY", label: "Clarity" },
  { key: "ARTIFACT", label: "Artifacts" },
  { key: "RELEVANCE", label: "Relevance" },
  // S5/P0-2/A4: the answer-leakage class — the failure human review exists to catch (a
  // stem-placed image that gives away the answer). `hint` is the DIMENSION_HINTS line the reject
  // chip carries as a tooltip. Must stay in sync with the backend REJECT_CATEGORIES set.
  { key: "ANSWER_LEAKAGE", label: "Answer leakage", hint: "Does not reveal the correct answer when shown pre-answer" },
  { key: "OTHER", label: "Other" },
];

// Classify a decideReviewQuestion response: 404 dark, 409 conflict (already decided —
// reload), other errStatus -> error, else ok. Mirrors assignmentOutcome.
export const decisionOutcome = classifyOutcomeWithConflict;

// --- QIR B4 / I1: decision-requires-evidence (UI mirror; the server is the authority) ---

// Every candidate in the group must carry the caller's own rating (usable/not-usable) before a
// group decision may be submitted. Empty group → not complete (nothing to decide). This drives the
// `disabled` state of the group-decision button; the server enforces the same rule (409
// RATINGS_INCOMPLETE), so a raced UI can never bypass it.
export function ratingsComplete(group) {
  const cands = (group?.candidates || []).filter(Boolean);
  if (cands.length === 0) return false;
  return cands.every((c) => {
    const s = savedRating(c);
    return Boolean(s?.rating);
  });
}

// The decision-confirm evidence summary, e.g. "You rated 3/3 candidates; winner checklist
// complete". The winner clause is added only when the picked winner is SPATIAL_CRITICAL (its
// checklist is the one that must be certified). winnerId is optional (REJECT_SET has no winner).
export function evidenceSummary(group, winnerId) {
  const cands = (group?.candidates || []).filter(Boolean);
  const total = cands.length;
  const rated = cands.filter((c) => {
    const s = savedRating(c);
    return Boolean(s?.rating);
  }).length;
  let summary = `You rated ${rated}/${total} candidates`;
  if (winnerId) {
    const winner = cands.find((c) => c.id === winnerId);
    if (winner && shouldRenderChecklist(winner)) {
      summary += `; winner checklist ${checklistComplete(winner) ? "complete" : "incomplete"}`;
    }
  }
  return summary;
}

// Build the decision request body (T-CMP B1 shape). dangerFlagIds: Set/array of item
// ids the tutor flagged. Returns null when the inputs cannot form a valid decision —
// the UI disables confirm on the same predicate, this is the belt-and-suspenders.
export function buildGroupDecision({ type, winnerId, category, note, dangerFlagIds }) {
  const dangerFlags = [...(dangerFlagIds || [])].map((itemId) => ({ itemId }));
  if (type === "PICK_BEST") {
    if (!winnerId) return null;
    return { type, winnerId, dangerFlags };
  }
  if (type === "REJECT_SET") {
    const cat = (category || "").trim();
    const line = (note || "").trim();
    if (!cat || !line || line.length > 200) return null;
    return { type, reason: { category: cat, note: line }, dangerFlags };
  }
  return null;
}

// --- S5/P0-2/A4: placement + answer-leakage model (pure helpers) ---

// Where a candidate image is shown to the student. STEM = with the question before answering
// (must not reveal the answer); EXPLANATION = in the post-answer explanation (the leak-safe
// default). Server stamps an explicit value at ingest and surfaces EXPLANATION for legacy items.
export const PLACEMENT = { STEM: "STEM", EXPLANATION: "EXPLANATION" };

// One candidate's placement, defaulting leak-safe: only an explicit STEM is STEM; absent/blank/
// unrecognized folds to EXPLANATION (matches the server's isStem-vs-default semantics).
export function candidatePlacement(candidate) {
  const raw = String(candidate?.payload?.placement || "").trim().toUpperCase();
  return raw === PLACEMENT.STEM ? PLACEMENT.STEM : PLACEMENT.EXPLANATION;
}

// The group's effective placement for the reviewer banner. Candidates share a question, so they
// normally agree; if they DISAGREE, show STEM (leak-safe dominance — any STEM makes the group
// leak-risky). Equivalent to "the first candidate's effective placement" when they all agree.
export function groupPlacement(group) {
  const cands = (group?.candidates || []).filter(Boolean);
  return cands.some((c) => candidatePlacement(c) === PLACEMENT.STEM)
    ? PLACEMENT.STEM
    : PLACEMENT.EXPLANATION;
}

// Do a group's candidates disagree on placement? Admin-only surface (the metadata panel shows
// each candidate's own placement, so a mix is visible there); the banner uses leak-safe dominance.
export function placementMixed(group) {
  const cands = (group?.candidates || []).filter(Boolean);
  if (cands.length < 2) return false;
  return new Set(cands.map(candidatePlacement)).size > 1;
}

// --- S3-U1 / S2-addendum D4 + R-V4: source-image grounding (pure helpers) ---

// Reference cap per question (R-DELTA-P2-1, config default 3). The backend enforces the
// cap at both link paths; the UI mirrors it belt-and-suspenders so a mis-emitted overflow
// never blows out the strip.
export const REFERENCE_CAP = 3;

// The derived grounding-state closed set (R-V4-A2). ADMIN-ONLY (the pipeline-compliance
// state) — the DTO only carries groundingStatus for admin callers; tutors never receive it.
export const GROUNDING_STATUS = {
  GROUNDED: "GROUNDED",
  UNGROUNDED: "UNGROUNDED",
  UNKNOWN_LEGACY: "UNKNOWN_LEGACY",
};

// The question's ACTIVE reference assets, capped at REFERENCE_CAP. Reviewer-visible for BOTH
// roles (the ground truth a reviewer judges anatomical fidelity against — D1). Null-safe.
export function groundingReferences(group) {
  const refs = group?.grounding?.references || [];
  return refs.filter(Boolean).slice(0, REFERENCE_CAP);
}

// Does this question have any reference on file? Drives expanded-by-default vs the none-on-file note.
export function hasGrounding(group) {
  return groundingReferences(group).length > 0;
}

// The "no reference images on file" note wording (R-V4-A9). The "(legacy batch)" parenthetical is
// asserted ONLY when the item is KNOWN legacy (groundingStatus === UNKNOWN_LEGACY, admin-only). A
// post-S3 UNGROUNDED item is NOT legacy — labeling it so would contradict the A2 honesty rationale.
// Tutors, who never receive groundingStatus, therefore get the neutral always-true wording rather
// than a possibly-false legacy claim. Returns null when references exist (the note never shows then).
export function groundingNoteText(group) {
  if (hasGrounding(group)) return null;
  return group?.groundingStatus === GROUNDING_STATUS.UNKNOWN_LEGACY
    ? "No reference images on file (legacy batch)"
    : "No reference images on file";
}

// --- QIR B7: VERIFICATION-class pointers (textbook / web citations) ---

// THE CLIENT DOES NOT CAP POINTERS, AND AN EARLIER VERSION OF THIS FILE WAS WRONG TO.
//
// It declared POINTER_CAP = 20 and sliced, reasoning that A-7's bound made truncation
// impossible: the cap counts TOTAL subdocs (active + tombstoned), the wire carries only
// ACTIVE ones, so active <= total <= 20. The gate on rr-38b1fa0a5a3a refuted the last step at
// source. `pointerCapPerQuestion` is a Spring @ConfigurationProperties field whose 20 is a
// DEFAULT, not an immutable maximum (ReviewQueueProperties.java:75), and the write path
// compares against the configured value with no upper validation. The true relation is
//
//     active <= total <= configuredCap
//
// so a supported runtime override of 21+ lets the server legitimately emit more than 20, and a
// hardcoded client slice would SILENTLY HIDE the remainder — the exact defect the old comment
// claimed to have ruled out, dressed as a safeguard.
//
// The server is the authority on the bound and already enforces it. Rendering everything it
// sends is therefore both simpler and strictly more honest: a citation list has no geometry to
// blow out (unlike the image strip REFERENCE_CAP protects), so there is nothing to defend
// against by dropping data. If a bound is ever wanted here it must be SERVER-PROVIDED or
// surfaced loudly — never a constant guessed on this side of the wire.
//
// The question's pointers. REVIEWER-VISIBLE FOR BOTH ROLES (B7 AMENDMENT A-10, which CORRECTS
// A-2's "admin-only" claim: `buildGroundingDto` is called unconditionally and the non-admin
// redactor copies the grounding block, omitting only groundingStatus — confirmed at source by
// the rr-38b1fa0a5a3a gate). Pointers therefore follow the REFERENCE parity pattern, not the
// admin-only groundingStatus/referenceRoles pattern. Null-safe.
//
// No client-side tombstone filter: every pointer DTO flows through activeGroundingPointers(),
// which includes only p.isActive(), so the wire carries actives only. A filter here would be
// dead code that reads like a safeguard — independently confirmed by the same gate.
export function groundingPointers(group) {
  const pointers = group?.grounding?.pointers || [];
  return pointers.filter(Boolean);
}

// Does this question carry any citation pointer?
// SCHEME GUARD FOR THE RENDERED href — F-D on gate rr-95d31897a802.
//
// A pointer URL is written by an admin (`@Secured("ROLE_ADMIN")`,
// ReviewQueueController.java:598-608) but READ BY BOTH ROLES: `buildGroundingDto` is
// called unconditionally and projects `.url(...)` with no isAdmin branch. The A-10
// parity this feature rests on is real, and it is exactly what turns an admin-written
// string into a live link in every tutor's browser.
//
// Nothing on the path validates the SCHEME. The server checks presence and character
// length only (ReviewQueueManager.addGroundingPointer, 1906-1935), and React 17 does
// not sanitize href — `javascript:...` was proved to reach the DOM verbatim through the
// real page with the suite's own tutor fixture. So the guard lives here, at the point
// of render, where it cannot be bypassed by a payload that predates it: anything that
// is not http/https yields null and NO ANCHOR IS RENDERED AT ALL.
//
// Deliberately an allowlist, not a `javascript:`-blocklist: `data:`, `vbscript:`, and
// scheme obfuscations are the same class of defect, and a blocklist would have to
// anticipate each one.
export function safePointerUrl(url) {
  if (typeof url !== "string" || !url.trim()) return null;
  try {
    const parsed = new URL(url.trim());
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? url.trim() : null;
  } catch {
    // Unparseable (including scheme-relative junk and bare text) is not a link.
    return null;
  }
}

export function hasPointers(group) {
  return groundingPointers(group).length > 0;
}

// Is there ANY grounding on file — images or citations?
//
// THIS EXISTS BECAUSE `hasGrounding` IS ABOUT IMAGES AND WAS BEING ASKED AN EMPTINESS QUESTION
// IT CANNOT ANSWER. The panel gated its whole body on `hasGrounding`, so a question with
// pointers but no reference images took the not-grounded branch and its pointers were DROPPED —
// exactly the state B7 creates (A-9 pins that the DTO returns a block for the pointer-only
// case). `hasGrounding` keeps its image-specific meaning; the panel's emptiness test moves here.
export function hasAnyGrounding(group) {
  return hasGrounding(group) || hasPointers(group);
}

// Admin-only per-candidate generation-role chips (D1/D5/R-V4-A7): provenance.referenceRoles is a
// {assetId: CONDITIONING|STYLE} map the pipeline DECLARES (labeled "declared by pipeline" in the UI).
// It rides inside the admin-only provenance map, so the redaction choke point strips it for tutors;
// this reader only ever runs inside the admin-gated metadata panel (belt + suspenders). Null-safe.
export function referenceRoleChips(candidate) {
  const roles = candidate?.provenance?.referenceRoles || {};
  return Object.keys(roles).map((assetId) => ({ assetId, role: roles[assetId] }));
}

// --- Keyboard shortcuts (nav + zoom only; verdict digit-picks retired) ---

export const SHORTCUT = {
  PREV: "prev",
  NEXT: "next",
  CLOSE_ZOOM: "closeZoom",
};

// Is a keyboard event's target a text-entry field? Shortcuts stay inert while one
// has focus (the reviewer is typing a note).
export function isEditableTarget(target) {
  if (!target) return false;
  const tag = String(target.tagName || "").toUpperCase();
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return Boolean(target.isContentEditable);
}

// Resolve a keydown into a navigation action, or null.
//   event: { key, ctrlKey, metaKey, altKey, editable }
//   uiState: { lightboxOpen }
// Esc closes the zoom lightbox when open (and while open it traps the keyboard).
// ←/→ navigate questions; inert while typing or with a modifier held. Verdict
// digit-picks and the Enter-submit chord are retired with the tournament UI.
export function resolveShortcut(event, uiState) {
  const e = event || {};
  const ui = uiState || {};
  const key = e.key;
  const anyModifier = Boolean(e.ctrlKey || e.metaKey || e.altKey);

  if (key === "Escape" || key === "Esc") {
    return ui.lightboxOpen ? { type: SHORTCUT.CLOSE_ZOOM } : null;
  }
  if (ui.lightboxOpen) return null;
  if (e.editable || anyModifier) return null;
  if (key === "ArrowLeft") return { type: SHORTCUT.PREV };
  if (key === "ArrowRight") return { type: SHORTCUT.NEXT };
  return null;
}

// --- Queue helpers (retained) ---

export function groupItemIds(group) {
  return (((group && group.candidates) || []).map((c) => c && c.id)).filter(Boolean);
}

export function mergeQueueItems(existing, incoming) {
  const seen = new Set((existing || []).map((it) => it && it.id));
  const merged = [...(existing || [])];
  (incoming || []).forEach((it) => {
    if (it && it.id != null && !seen.has(it.id)) {
      seen.add(it.id);
      merged.push(it);
    }
  });
  return merged;
}

// The session progress line, in QUESTIONS (groups) — the same unit the header and
// rail count in (#584 defect 3).
export function groupProgress(counts) {
  const c = counts || {};
  const reviewed = c.reviewedGroups || 0;
  const toReview = c.toReviewGroups || 0;
  const base =
    `${reviewed} question${reviewed === 1 ? "" : "s"} reviewed this session` +
    ` · ${toReview} to review`;
  return c.moreOnServer ? `${base} · more will load` : base;
}

// --- Metadata (retained: admin-only reference chrome) ---

/**
 * The verdict on payload.verification is SELF-REPORTED BY THE GENERATION PIPELINE.
 * Nothing inspects the image: qlearn-visuals-pool-validate.py asserts the field
 * reads CLEAN as an ingest gate, and no service code computes it. The independent
 * pre-screen in docs/qlearn-visuals-pipeline-design.md §13 was never built.
 *
 * A 2026-08-03 groundedness run over the whole live pool found 13 of 24
 * pipeline-"CLEAN" candidates defective, 11 with errors that contradict the
 * question's own keyed answer (evidence/2026-08-03-image-review-groundedness-findings.md
 * in qlearn-unified). So this verdict must never be worded as though a screen ran.
 */
export const UNVERIFIED_SCREENING_NOTE = "not independently verified";

export function screeningVerdict(candidate) {
  const p = candidate?.payload || {};
  return p.verificationVerdict || p.verification?.verdict || null;
}

export function modelInLoopSummary(candidate) {
  if (!candidate) return null;
  const prov = candidate.provenance || {};
  const p = candidate.payload || {};
  const gen = prov.model || p.model || null;
  const verdict = screeningVerdict(candidate);
  const parts = [];
  if (gen) parts.push(`generated by ${gen}`);
  parts.push(
    verdict
      ? `generator self-reported "${verdict}" · ${UNVERIFIED_SCREENING_NOTE}`
      : `no screening verdict · ${UNVERIFIED_SCREENING_NOTE}`
  );
  return parts.join(" · ");
}

export function metadataRows(candidate) {
  if (!candidate) return [];
  const prov = candidate.provenance || {};
  const p = candidate.payload || {};
  const verdict = screeningVerdict(candidate);
  const selfReport = verdict ? `${verdict} (${UNVERIFIED_SCREENING_NOTE})` : null;
  const rows = [
    ["Model", prov.model || p.model],
    ["Generation batch", prov.generationBatchId],
    ["Prompt", prov.prompt],
    ["Cost", prov.cost != null ? String(prov.cost) : null],
    ["Candidate", p.candidateId],
    ["Type", p.candidateType],
    // S5/P0-2/A4: per-candidate placement in the admin metadata panel. Because it is rendered per
    // candidate, a group where candidates DISAGREE on placement shows the mix here (the banner
    // itself uses leak-safe STEM dominance and stays a single line).
    ["Placement", p.placement],
    // Labelled by its provenance, not as a screening result — see
    // UNVERIFIED_SCREENING_NOTE. "Screening: CLEAN" read as though something had
    // checked the image; nothing had.
    ["Generator self-report", selfReport],
    ["Ingested", candidate.createDt],
    ["Review status", candidate.reviewStatus],
    ["Decided", candidate.reviewDecidedAt],
    ["Decided by", candidate.reviewDecidedBy],
    ["Publish status", candidate.publishStatus],
    ["Published", candidate.publishedAt],
    ["Published by", candidate.publishedBy],
    ["Publish batch", candidate.publishBatchId],
    ["Retracted", candidate.retractedAt],
  ];
  return rows
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(([label, value]) => ({ label, value: String(value) }));
}
